'use es6';

import { debounce, zip, omit, pick } from 'underscore';
import SeamlessImmutable from 'seamless-immutable';
import { getPreviousCustomWidgetBufferFields, getCustomWidgetBufferFieldsMap, makeGetMessages, makeGetMasterLanguage, getCustomWidgetLabel } from 'DesignData/selectors/customWidgetBuffers';
import { generateTranslationsFromFields, generateAllPaths } from 'DesignData/helpers/TranslationHelpers';
import { getFieldWithTranslationPath } from 'DesignData/helpers/customWidgetFieldsHelper';
import { updateMessages, updateTranslation } from 'DesignData/actions/CustomWidgetBuffers';
import { CUSTOM_WIDGET } from 'DesignData/constants/FolderAssetTypes';
import { CUSTOM_WIDGET_FIELD_CREATE, CUSTOM_WIDGET_FIELD_CREATE_GROUP, CUSTOM_WIDGET_FIELD_CLONE, CUSTOM_WIDGET_FIELD_DELETE, CUSTOM_WIDGET_FIELD_EDIT, CUSTOM_WIDGET_FIELD_MOVE, FINDER_FILE_RENAME } from 'DesignData/actions/ActionTypes';
const getMessages = makeGetMessages();
const getMasterLanguage = makeGetMasterLanguage();

const getUpdatedMessages = (previousFieldAndPath, currentFieldAndPath, messages) => {
  const previousPaths = generateAllPaths(previousFieldAndPath.field, previousFieldAndPath.path);
  const currentPaths = generateAllPaths(currentFieldAndPath.field, currentFieldAndPath.path);
  /* Immediate update to field name */

  /* This requires that we go and update all translatable fields */

  /* Handle fields with default as an object, and also handle groups which have children
     and those children could have children */

  /* Now we have to update every single message, this is going to get gross */

  let newMessages = SeamlessImmutable({});

  for (const key of Object.keys(messages)) {
    const translation = messages[key];
    const contentWithNewKeys = currentPaths.reduce((hash, path, index) => {
      const previousValue = translation[previousPaths[index]];

      if (previousValue && previousValue.message) {
        return hash.setIn([path, 'message'], previousValue.message);
      }
      /* Incase translations does not exist previously */


      return hash.setIn([path, 'message'], '');
    }, SeamlessImmutable({}));
    newMessages = newMessages.set(key, translation.without(previousPaths).merge(contentWithNewKeys, {
      deep: true
    }));
  }
  /* We want to update all messages at the same time */


  return newMessages;
};

const debouncedUpdateFieldTranslations = debounce((dispatch, getState, customWidgetId, customWidgetLabel, currentFieldAndPath, fieldDiffs) => {
  const {
    field,
    path
  } = currentFieldAndPath;
  const updatedTranslations = generateTranslationsFromFields(customWidgetLabel, [Object.assign({}, pick(field, Object.keys(fieldDiffs)), {
    name: path
  })]);
  /* We only want to change the content of the english, not any other file */

  const masterLanguage = getMasterLanguage(getState(), {
    customWidgetId
  });
  dispatch(updateTranslation(customWidgetId, masterLanguage, updatedTranslations));
}, 500);

const updateFieldTranslations = (dispatch, getState, result) => {
  const {
    field,
    customWidgetId
  } = result;
  const state = getState();
  const previousFields = getPreviousCustomWidgetBufferFields(state, {
    customWidgetId
  });

  if (previousFields) {
    const customWidgetLabel = getCustomWidgetLabel(state, {
      customWidgetId
    });
    const currentFields = getCustomWidgetBufferFieldsMap(state, {
      customWidgetId
    });
    const previousFieldAndPath = getFieldWithTranslationPath(previousFields.fields, field.id);
    const currentFieldAndPath = getFieldWithTranslationPath(currentFields, field.id);

    if (previousFieldAndPath.field.name === field.name) {
      const fieldDiffs = omit(currentFieldAndPath.field, (value, key) => {
        return previousFieldAndPath.field[key] === value;
      });
      debouncedUpdateFieldTranslations(dispatch, getState, customWidgetId, customWidgetLabel, currentFieldAndPath, fieldDiffs);
    } else {
      /* Immediate update to field name */
      const messages = getMessages(state, {
        customWidgetId
      });
      const newMessages = getUpdatedMessages(previousFieldAndPath, currentFieldAndPath, messages);
      dispatch(updateMessages(customWidgetId, newMessages));
    }
  }
};

const moveTranslations = (dispatch, getState, result) => {
  const {
    fieldId,
    customWidgetId
  } = result;
  const state = getState();
  const previousFields = getPreviousCustomWidgetBufferFields(state, {
    customWidgetId
  });

  if (previousFields) {
    const currentFields = getCustomWidgetBufferFieldsMap(state, {
      customWidgetId
    });
    const previousFieldAndPath = getFieldWithTranslationPath(previousFields.fields, fieldId);
    const currentFieldAndPath = getFieldWithTranslationPath(currentFields, fieldId);
    if (previousFieldAndPath.path === currentFieldAndPath.path) return;
    const messages = getMessages(state, {
      customWidgetId
    });
    const newMessages = getUpdatedMessages(previousFieldAndPath, currentFieldAndPath, messages);
    dispatch(updateMessages(customWidgetId, newMessages));
  }
};

const deleteTranslations = (dispatch, getState, result) => {
  const {
    fieldId,
    customWidgetId
  } = result;
  const state = getState();
  const previousFields = getPreviousCustomWidgetBufferFields(state, {
    customWidgetId
  });

  if (previousFields) {
    const previousFieldAndPath = getFieldWithTranslationPath(previousFields.fields, fieldId);
    const previousPaths = generateAllPaths(previousFieldAndPath.field, previousFieldAndPath.path);
    const messages = getMessages(state, {
      customWidgetId
    });
    /* Update all messages */

    const updatedMessages = Object.keys(messages).reduce((hash, langCode) => {
      return hash.update(langCode, translations => translations.without(previousPaths));
    }, messages);
    dispatch(updateMessages(customWidgetId, updatedMessages));
  }
};

const addTranslations = (dispatch, getState, result) => {
  const {
    customWidgetId
  } = result;
  const field = result.clonedField || result.field;
  const state = getState();
  const customWidgetLabel = getCustomWidgetLabel(state, {
    customWidgetId
  });
  const currentFields = getCustomWidgetBufferFieldsMap(state, {
    customWidgetId
  });
  const currentFieldAndPath = getFieldWithTranslationPath(currentFields, field.id, false);
  const baseTranslations = generateTranslationsFromFields(customWidgetLabel, [currentFieldAndPath.field]);
  const masterLanguage = getMasterLanguage(state, {
    customWidgetId
  });
  /* We want to add these translations to every single translations file */

  const newTranslations = Object.keys(baseTranslations).reduce((hash, path) => {
    const fullPath = currentFieldAndPath.path ? `${currentFieldAndPath.path}.${path}` : path;
    return hash.set(fullPath, baseTranslations[path]);
  }, SeamlessImmutable({}));
  const messages = getMessages(state, {
    customWidgetId
  });
  const updatedMessages = Object.keys(messages).reduce((hash, langCode) => {
    if (masterLanguage === langCode) {
      return hash.update(langCode, translations => translations.merge(Object.keys(newTranslations).reduce((__hash, path) => {
        return __hash.set(path, {
          description: '',
          message: __hash[path].message
        });
      }, newTranslations)));
    }

    return hash.update(langCode, translations => translations.merge(newTranslations));
  }, messages);
  dispatch(updateMessages(customWidgetId, updatedMessages));
};

const groupTranslations = (dispatch, getState, result) => {
  const {
    fieldGroup,
    children,
    customWidgetId
  } = result;
  const state = getState();
  const previousFields = getPreviousCustomWidgetBufferFields(state, {
    customWidgetId
  });

  if (previousFields) {
    const customWidgetLabel = getCustomWidgetLabel(state, {
      customWidgetId
    });
    const currentFields = getCustomWidgetBufferFieldsMap(state, {
      customWidgetId
    });
    const pathPairs = Object.keys(children).reduce((hash, id) => {
      const previousChildFieldAndPath = getFieldWithTranslationPath(previousFields.fields, id);
      const previousChildPaths = generateAllPaths(previousChildFieldAndPath.field, previousChildFieldAndPath.path);
      const currentChildFieldAndPath = getFieldWithTranslationPath(currentFields, id);
      const currentChildPaths = generateAllPaths(currentChildFieldAndPath.field, currentChildFieldAndPath.path);
      return hash.concat(zip(previousChildPaths, currentChildPaths));
    }, []);
    const messages = getMessages(state, {
      customWidgetId
    });
    const currentFieldGroup = getFieldWithTranslationPath(currentFields, fieldGroup.id);
    const baseTranslations = generateTranslationsFromFields(customWidgetLabel, [currentFieldGroup.field]);
    const currentGroupPaths = getFieldWithTranslationPath(currentFields, currentFieldGroup.field.id, false);
    const newTranslations = Object.keys(baseTranslations).reduce((hash, path) => {
      const fullPath = currentGroupPaths.path ? `${currentGroupPaths.path}.${path}` : path;
      return hash.set(fullPath, baseTranslations[path]);
    }, SeamlessImmutable({}));
    const updatedMessages = Object.keys(messages).reduce((hash, langCode) => {
      const translations = messages[langCode];
      const updatedTranslations = pathPairs.reduce((translationsHash, pair) => {
        const [oldPath, newPath] = pair;
        return translationsHash.set(newPath, translations[oldPath]).without(oldPath);
      }, translations).merge(newTranslations, {
        deep: true
      });
      return hash.set(langCode, updatedTranslations);
    }, messages);
    dispatch(updateMessages(customWidgetId, updatedMessages));
  }
};

const updateModuleNameTranslations = (dispatch, getState, result) => {
  const {
    fileType,
    fileId: customWidgetId,
    fileName
  } = result;

  if (fileType === CUSTOM_WIDGET) {
    const state = getState();
    const updatedTranslations = {
      name: {
        message: fileName
      }
    };
    const masterLanguage = getMasterLanguage(state, {
      customWidgetId
    });
    dispatch(updateTranslation(customWidgetId, masterLanguage, updatedTranslations));
  }
};

export default (({
  dispatch,
  getState
}) => next => action => {
  const result = next(action);
  /* fileId is passed through when renaming a module */

  const customWidgetId = result.customWidgetId || result.fileId;
  if (!customWidgetId) return result;
  const masterLanguage = getMasterLanguage(getState(), {
    customWidgetId
  });
  if (!masterLanguage) return result;

  switch (result.type) {
    case CUSTOM_WIDGET_FIELD_CREATE:
    case CUSTOM_WIDGET_FIELD_CLONE:
      {
        addTranslations(dispatch, getState, result);
        break;
      }

    case CUSTOM_WIDGET_FIELD_CREATE_GROUP:
      {
        groupTranslations(dispatch, getState, result);
        break;
      }

    case CUSTOM_WIDGET_FIELD_DELETE:
      {
        deleteTranslations(dispatch, getState, result);
        break;
      }

    case CUSTOM_WIDGET_FIELD_EDIT:
      {
        updateFieldTranslations(dispatch, getState, result);
        break;
      }

    case CUSTOM_WIDGET_FIELD_MOVE:
      {
        moveTranslations(dispatch, getState, result);
        break;
      }

    case FINDER_FILE_RENAME:
      {
        updateModuleNameTranslations(dispatch, getState, result);
        break;
      }

    default:
      break;
  }

  return result;
});